import { Eye, Upload, MoreVertical, Palette, PawPrint, Shield, WandSparkles } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { ScryfallCard } from '@/types/scryfall';
import { cn } from '@/lib/utils';
import { useState, useMemo } from 'react';
import { PrintingSelector } from './PrintingSelector';
import { CardDetailsDialog } from './CardDetailsDialog';
import { DeckImportDialog } from './DeckImportDialog';
import { MTGCard } from './MTGCard';
import { CardActions } from './CardActions';
import { QuickAddBar } from './QuickAddBar';

interface CardListProps {
  cards: (ScryfallCard & { count: number })[];
  sideboardCards?: (ScryfallCard & { count: number })[];
  addCard: (card: ScryfallCard) => void;
  removeCard: (card: ScryfallCard) => void;
  setCardCount: (card: ScryfallCard, count: number) => void;
  updateCard: (oldCardId: string, newCard: ScryfallCard) => void;
  setBannerUrl: (url: string | null) => void;
  commanderCardIds: string[];
  setCommanderCardIds: (ids: string[]) => void;
  companionCardId: string | null;
  setCompanionCardId: (id: string | null) => void;
  openDeckBuilder: () => void;
  isSideboard?: boolean;
  onMoveCard?: (card: ScryfallCard) => void;
  setSideboardCards?: (cards: ScryfallCard[]) => void;
  children?: React.ReactNode;
}

const getCardType = (typeLine: string): string => {
  const mainType = typeLine.split(' — ')[0];
  if (mainType.includes('Creature')) return 'Creature';
  if (mainType.includes('Planeswalker')) return 'Planeswalker';
  if (mainType.includes('Instant')) return 'Instant';
  if (mainType.includes('Sorcery')) return 'Sorcery';
  if (mainType.includes('Artifact')) return 'Artifact';
  if (mainType.includes('Enchantment')) return 'Enchantment';
  if (mainType.includes('Land')) return 'Land';
  return 'Other';
};

const CardRow = ({ card, ...props }: { card: ScryfallCard & { count: number } } & Omit<CardListProps, 'cards' | 'openDeckBuilder' | 'updateCard'> & { setDetailsCard: (card: ScryfallCard) => void; setSelectedCard: (card: ScryfallCard) => void; onMoveCard?: (card: ScryfallCard) => void; isSideboard?: boolean; }) => {
  const {
    addCard,
    removeCard,
    setCardCount,
    setBannerUrl,
    commanderCardIds,
    setCommanderCardIds,
    companionCardId,
    setCompanionCardId,
    setDetailsCard,
    setSelectedCard,
    onMoveCard
  } = props;

  const getRarityColor = (rarity: string) => {
    switch (rarity) {
      case 'mythic': return 'bg-gradient-to-r from-orange-500/20 to-red-500/20 text-orange-400 border-orange-500/30';
      case 'rare': return 'bg-gradient-to-r from-yellow-500/20 to-orange-500/20 text-yellow-400 border-yellow-500/30';
      case 'uncommon': return 'bg-gradient-to-r from-gray-400/20 to-gray-500/20 text-gray-300 border-gray-400/30';
      case 'common': return 'bg-gradient-to-r from-gray-600/20 to-gray-700/20 text-gray-400 border-gray-600/30';
      default: return 'bg-gradient-to-r from-gray-500/20 to-gray-600/20 text-gray-400 border-gray-500/30';
    }
  };

  return (
    <div
      className="group flex items-center gap-2 sm:gap-3 p-1.5 sm:p-2 rounded-lg sm:rounded-xl bg-gradient-to-r from-accent/30 to-accent/10 hover:from-accent/50 hover:to-accent/20 transition-all duration-200 border border-border/30 hover:border-border/50"
    >
      <div className="w-12 h-16 sm:w-14 sm:h-20 shrink-0" onClick={() => setDetailsCard(card)} aria-label={`View details for ${card.name}`}>
        {card.image_uris?.small ? (
          <MTGCard
            card={card}
            showCount={false}
            enable3D={true}
            className="rounded-md sm:rounded-lg bg-gradient-to-br from-muted to-muted/50 border border-border/50 cursor-pointer h-full"
            imageClassName="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center rounded-md sm:rounded-lg bg-gradient-to-br from-muted to-muted/50 border border-border/50">
            <Eye className="h-5 w-5 text-muted-foreground/50" />
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <h4 className="font-semibold text-foreground text-xs sm:text-sm leading-tight line-clamp-2 cursor-pointer" onClick={() => setDetailsCard(card)}>
            {card.name}
          </h4>
          <div className="flex items-center shrink-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSelectedCard(card)}
              className={cn("h-6 w-6")}
            >
              <Palette className="h-4 w-4" />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-6 w-6">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {commanderCardIds.includes(card.id) ? (
                  <DropdownMenuItem onClick={() => setCommanderCardIds(commanderCardIds.filter(id => id !== card.id))}>
                    <Shield className="h-4 w-4 mr-2" />
                    Remove as Commander
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onClick={() => {
                    if (commanderCardIds.length < 2) {
                      setCommanderCardIds([...commanderCardIds, card.id]);
                    }
                  }}>
                    <Shield className="h-4 w-4 mr-2" />
                    Add as Commander
                  </DropdownMenuItem>
                )}
                {companionCardId === card.id ? (
                  <DropdownMenuItem onClick={() => setCompanionCardId(null)}>
                    <PawPrint className="h-4 w-4 mr-2" />
                    Unset as Companion
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onClick={() => setCompanionCardId(card.id)}>
                    <PawPrint className="h-4 w-4 mr-2" />
                    Set as Companion
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => setBannerUrl(card.image_uris?.art_crop || null)}>
                  <Palette className="h-4 w-4 mr-2" />
                  Set as Banner
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onMoveCard && onMoveCard(card)}>
                  Move to {props.isSideboard ? 'Mainboard' : 'Sideboard'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <p className="text-xs text-muted-foreground line-clamp-1">
          {card.type_line}
        </p>

        <div className="flex items-center justify-between">
          <Badge
            variant="outline"
            className={`text-xs font-medium ${getRarityColor(card.rarity)}`}
          >
            {card.rarity}
          </Badge>
          <span className="text-xs text-muted-foreground font-medium">
            {card.set.toUpperCase()}
          </span>
        </div>
      </div>

      <CardActions
        card={card}
        addCard={addCard}
        removeCard={removeCard}
        setCardCount={setCardCount}
        count={card.count}
      />
    </div>
  );
};

export function CardList(props: CardListProps) {
  const { cards, sideboardCards, addCard, removeCard, setCardCount, updateCard, setBannerUrl, commanderCardIds, setCommanderCardIds, companionCardId, setCompanionCardId, openDeckBuilder, isSideboard, onMoveCard, setSideboardCards: setSideboard, children } = props;
  const [selectedCard, setSelectedCard] = useState<ScryfallCard | null>(null);
  const [detailsCard, setDetailsCard] = useState<ScryfallCard | null>(null);

  const mainboardCardCount = useMemo(() => {
    return cards.reduce((acc, card) => acc + card.count, 0);
  }, [cards]);

  const sideboardCardCount = useMemo(() => {
    return sideboardCards ? sideboardCards.reduce((acc, card) => acc + card.count, 0) : 0;
  }, [sideboardCards]);

  const groupedAndSortedCards = useMemo(() => {
    if (isSideboard) {
      return cards.length > 0 ? [{
        groupName: '',
        items: cards,
        count: cards.reduce((acc, item) => acc + item.count, 0),
      }] : [];
    }

    const groups: Record<string, (ScryfallCard & { count: number })[]> = {
      Commander: [],
      Companion: [],
      Creature: [],
      Planeswalker: [],
      Sorcery: [],
      Instant: [],
      Artifact: [],
      Enchantment: [],
      Land: [],
      Other: [],
    };

    cards.forEach(item => {
      if (commanderCardIds.includes(item.id)) {
        groups.Commander.push(item);
      } else if (companionCardId === item.id) {
        groups.Companion.push(item);
      } else {
        const type = getCardType(item.type_line);
        groups[type].push(item);
      }
    });

    return Object.entries(groups)
      .filter(([, items]) => items.length > 0)
      .map(([groupName, items]) => ({
        groupName,
        items,
        count: items.reduce((acc, item) => acc + item.count, 0),
      }));
  }, [cards, commanderCardIds, companionCardId, isSideboard]);

  const [isImporting, setIsImporting] = useState(false);

  if (cards.length === 0) {
    if (isSideboard) return null;
    return (
      <>
        <div className="hidden sm:flex flex-grow mb-2">
          <QuickAddBar onCardAdded={addCard} />
        </div>
        <Card className="border-dashed border-border/50 bg-accent/10 h-[calc(100vh-13rem)] lg:h-[95%]">
          <CardContent className="py-4 px-4 text-center flex flex-col justify-center h-full">
            <div className="max-w-sm mx-auto space-y-4">
              <div className="flex w-12 h-12 bg-gradient-to-br from-primary/20 to-primary/5 rounded-2xl items-center justify-center mx-auto border border-primary/20">
                <Eye className="h-6 w-6 text-primary" />
              </div>
              <div className="space-y-2">
                <h3 className="text-md font-semibold text-foreground">Your list is empty</h3>
                <p className="text-muted-foreground text-xs leading-relaxed">
                  Use the search to find cards and add them to your deck, or import a list.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button variant="outline" size="sm" className="w-full" onClick={() => setIsImporting(true)}>
                  <Upload className="h-4 w-4 mr-2" />
                  Import List
                </Button>
              </div>
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">
                    Or
                  </span>
                </div>
              </div>
              <div className="text-center">
                <h4 className="text-md font-semibold text-foreground">Building a new Commander deck?</h4>
                <p className="text-muted-foreground text-xs mt-1">Let our assistant help you get started.</p>
                <Button size="sm" className="w-full mt-3" onClick={openDeckBuilder}>
                  <WandSparkles className="h-4 w-4 mr-2" />
                  Try our Deck Builder
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        <DeckImportDialog open={isImporting} onOpenChange={setIsImporting} setCards={(cards) => cards.forEach(addCard)} setSideboardCards={setSideboard || (() => {})} />
      </>
    );
  }

  return (
    <>
      <Card className="bg-transparent border-none shadow-none">
        <CardContent className="space-y-3 h-full overflow-y-auto px-1">
          <div className="hidden sm:flex justify-between items-center mb-2">
            {!isSideboard && (
              <div className="flex items-center justify-between w-full">
                <h3 className="font-semibold text-lg text-foreground">
                  Deck ({mainboardCardCount})
                  {sideboardCardCount > 0 && `, Sideboard (${sideboardCardCount})`}
                </h3>
                {children}
              </div>
            )}
          </div>
          {!isSideboard && (
            <div className="hidden sm:flex flex-grow">
              <QuickAddBar onCardAdded={addCard} />
            </div>
          )}
          {groupedAndSortedCards.map(({ groupName, items, count }) => (
            <div key={groupName}>
              {groupName && (
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-semibold text-sm text-foreground">{`${groupName} (${count})`}</h3>
                </div>
              )}
              <div className="space-y-2">
                {items.map((item) => (
                  <CardRow
                    key={`${item.id}-${item.set}-${item.collector_number}-${item.is_foil}`}
                    card={item}
                    addCard={addCard}
                    removeCard={removeCard}
                    setCardCount={setCardCount}
                    setBannerUrl={setBannerUrl}
                    commanderCardIds={commanderCardIds}
                    setCommanderCardIds={setCommanderCardIds}
                    companionCardId={companionCardId}
                    setCompanionCardId={setCompanionCardId}
                    setDetailsCard={setDetailsCard}
                    setSelectedCard={setSelectedCard}
                    onMoveCard={onMoveCard}
                    isSideboard={isSideboard}
                  />
                ))}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
      {selectedCard && (
        <PrintingSelector
          card={selectedCard}
          open={!!selectedCard}
          onOpenChange={(open) => !open && setSelectedCard(null)}
          onSelect={(oldCardId, newCard) => updateCard(oldCardId, newCard)}
        />
      )}
      {detailsCard && (
        <CardDetailsDialog
          card={detailsCard}
          open={!!detailsCard}
          onOpenChange={(open) => !open && setDetailsCard(null)}
          onAddToList={addCard}
        />
      )}
    </>
  );
}
